import * as z from 'zod';

/**
 * Build-time configuration for the active flavour.
 *
 * CRA bakes `process.env.REACT_APP_*` into the bundle at build time, so these
 * values are fixed for a given build — changing an env file requires a rebuild.
 * Which file is read is decided by the script that runs:
 *
 *   npm start                -> .env.development
 *   npm run build            -> .env.production
 *   npm run build:development -> .env.development
 *
 * Each lookup below must stay a literal `process.env.REACT_APP_X` expression;
 * webpack substitutes them textually, so a computed key resolves to undefined.
 */
const schema = z.object({
  env: z.enum(['development', 'production']).default('development'),
  apiUrl: z.string().url(),
  apiBaseUrl: z.string().url(),
  tokenKey: z.string().min(1).default('ADMIN_APP_TOKEN'),
  rowsPerPage: z.coerce.number().int().positive().default(25),
});

const envNames: Record<string, string> = {
  env: 'REACT_APP_ENV',
  apiUrl: 'REACT_APP_API_URL',
  apiBaseUrl: 'REACT_APP_API_BASE_URL',
  tokenKey: 'REACT_APP_TOKEN_KEY',
  rowsPerPage: 'REACT_APP_PAGINATION_ROWS_PER_PAGE',
};

const result = schema.safeParse({
  env: process.env.REACT_APP_ENV,
  apiUrl: process.env.REACT_APP_API_URL,
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL,
  tokenKey: process.env.REACT_APP_TOKEN_KEY,
  rowsPerPage: process.env.REACT_APP_PAGINATION_ROWS_PER_PAGE,
});

if (!result.success) {
  const details = result.error.issues
    .map((issue) => `  ${envNames[String(issue.path[0])] ?? issue.path.join('.')}: ${issue.message}`)
    .join('\n');

  // Fail loudly rather than shipping `undefined` into Apollo or fetch.
  throw new Error(`Invalid environment configuration:\n${details}`);
}

export const config = result.data;

export const isProduction = config.env === 'production';
